import React, { useEffect, useState } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import './LocationManagers.css';

interface Location {
  id: number;
  name: string;
  city: string;
  max_recurrence_weeks?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

interface LocationRecurrenceSettingProps {
  location: Location;
  /** Zodat het overzicht in het beheerpaneel de nieuwe waarde meteen meeneemt. */
  onSaved?: (maxRecurrenceWeeks: number) => void;
}

const MIN_WEEKS = 1;
const MAX_WEEKS = 52;
const FALLBACK_WEEKS = 12;

/**
 * Hoeveel weken vooruit iemand bij deze locatie een wekelijks herhalende
 * reservering mag plannen. Losse reserveringen vallen hier niet onder; die mogen
 * tot een jaar vooruit.
 */
const LocationRecurrenceSetting: React.FC<LocationRecurrenceSettingProps> = ({
  location,
  onSaved,
}) => {
  const [weeks, setWeeks] = useState<string>(String(location.max_recurrence_weeks ?? FALLBACK_WEEKS));
  const [savedWeeks, setSavedWeeks] = useState<number>(location.max_recurrence_weeks ?? FALLBACK_WEEKS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Het overzicht in het beheerpaneel heeft dit veld niet altijd bij zich, dus
  // halen we de locatie er zelf bij als de waarde ontbreekt.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (location.max_recurrence_weeks !== undefined) {
        setWeeks(String(location.max_recurrence_weeks));
        setSavedWeeks(location.max_recurrence_weeks);
        return;
      }

      setIsLoading(true);
      try {
        const response = await apiClient.get<ApiResponse<Location>>(`/locations/${location.id}`);
        const value = response.data.data?.max_recurrence_weeks ?? FALLBACK_WEEKS;
        if (!cancelled) {
          setWeeks(String(value));
          setSavedWeeks(value);
        }
      } catch (err) {
        if (!cancelled) {
          setError('De huidige instelling kon niet geladen worden.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [location.id, location.max_recurrence_weeks]);

  const parsed = parseInt(weeks, 10);
  const isValid = Number.isInteger(parsed) && parsed >= MIN_WEEKS && parsed <= MAX_WEEKS;
  const isChanged = isValid && parsed !== savedWeeks;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      setError(`Vul een aantal weken in tussen ${MIN_WEEKS} en ${MAX_WEEKS}.`);
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.put<ApiResponse<Location>>(`/locations/${location.id}/recurrence-setting`, {
        max_recurrence_weeks: parsed,
      });
      setSavedWeeks(parsed);
      setSuccess(`Opgeslagen. Herhalingen mogen hier maximaal ${parsed} ${parsed === 1 ? 'week' : 'weken'} vooruit lopen.`);
      onSaved?.(parsed);
    } catch (err) {
      const details = (err as { response?: { data?: { details?: string[] } } })?.response?.data?.details;
      setError(
        permissionMessage(err) ||
          (Array.isArray(details) ? details.join(', ') : null) ||
          'De instelling kon niet opgeslagen worden.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="location-managers">
      <div className="managers-intro">
        <h2>Vooruit plannen bij {location.name}</h2>
        <p>
          Iemand kan een fiets elke week op dezelfde dag en tijd laten terugkomen. Hier bepaalt u hoe
          ver vooruit dat mag. Bij 12 weken kan de laatste keer van een herhaling dus op zijn vroegst
          vandaag plus 12 weken vallen. Losse reserveringen vallen hier buiten; die mogen tot een jaar
          vooruit.
        </p>
      </div>

      {error && <div className="managers-message managers-message-error">{error}</div>}
      {success && <div className="managers-message managers-message-success">{success}</div>}

      {isLoading ? (
        <p className="managers-loading">Instelling laden...</p>
      ) : (
        <form className="manager-form" onSubmit={save}>
          <div className="manager-form-fields">
            <div className="manager-form-group">
              <label htmlFor={`max-recurrence-weeks-${location.id}`}>Aantal weken vooruit</label>
              <input
                id={`max-recurrence-weeks-${location.id}`}
                type="number"
                min={MIN_WEEKS}
                max={MAX_WEEKS}
                step={1}
                value={weeks}
                onChange={(event) => setWeeks(event.target.value)}
                disabled={isSaving}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isSaving || !isChanged}>
              {isSaving ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
          <p className="manager-form-hint">
            Minimaal {MIN_WEEKS} en maximaal {MAX_WEEKS} weken. Nu ingesteld op {savedWeeks}{' '}
            {savedWeeks === 1 ? 'week' : 'weken'}. Een lagere waarde raakt bestaande herhalingen niet;
            die blijven staan zoals ze zijn.
          </p>
        </form>
      )}
    </div>
  );
};

export default LocationRecurrenceSetting;
